import React, { useEffect, useMemo, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const toDigits = (value, digits) => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(value)).toString();
  const padded = abs.padStart(digits, '0').slice(-digits);
  return { sign, chars: padded.split('') };
};

const Digit = ({ target, delayMs, durationMs, reduced }) => {
  const [rendered, setRendered] = useState(target);
  const prev = useRef(target);

  useEffect(() => {
    if (prev.current === target) return;
    prev.current = target;
    if (reduced) {
      setRendered(target);
      return;
    }
    const start = performance.now() + delayMs;
    const from = Number(rendered) || 0;
    const to = Number(target) || 0;
    let rafId;
    const step = (t) => {
      const elapsed = t - start;
      if (elapsed < 0) {
        rafId = requestAnimationFrame(step);
        return;
      }
      const p = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = Math.round(from + (to - from) * eased);
      setRendered(String(((cur % 10) + 10) % 10));
      if (p < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [target, delayMs, durationMs, reduced]);

  return <span style={{ display: 'inline-block', width: '0.6em', textAlign: 'center' }}>{rendered}</span>;
};

const TickerNumber = ({
  value,
  digits = 6,
  prefix = '',
  suffix = '',
  durationMs = 600,
  className = '',
}) => {
  const safeDigits = Math.max(1, Math.min(12, digits));
  const reduced = useMemo(prefersReducedMotion, []);
  const { sign, chars } = toDigits(Number(value) || 0, safeDigits);

  // Count leading zeros to dim them.
  let leadingZeroCount = 0;
  for (let i = 0; i < chars.length - 1; i++) {
    if (chars[i] === '0') leadingZeroCount++;
    else break;
  }

  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {prefix}
      {sign}
      {chars.map((c, i) => {
        const isLeadingZero = i < leadingZeroCount;
        // Right-most digit cell delay = 0, left-most = largest.
        const delayMs = (chars.length - 1 - i) * 60;
        return (
          <span key={i} style={{ opacity: isLeadingZero ? 0.2 : 1 }}>
            <Digit target={c} delayMs={delayMs} durationMs={durationMs} reduced={reduced} />
          </span>
        );
      })}
      {suffix}
    </span>
  );
};

export default TickerNumber;
